/**
 * Every URL the tools own.
 *
 * The other half of what used to be one table. These are the pages the GTM thesis rests on:
 * programmatic SEO on deploy-time intent, one URL per subject, each a document a crawler can read
 * without executing anything. `loads` is why — the prerenderer fetches each entry and serializes
 * it into the page, so the incidents, profiles, taxonomy and figures are IN the HTML rather than
 * the word "Loading…".
 *
 * THE PATHS ARE AT THE ROOT and not under `/tools/`, unlike authoxi's gtm build. authoxi chose a
 * path prefix so links from its landing page stay same-site; agitome's tools were indexed at the
 * root first, and moving them to make the build boundary tidier would rewrite every indexed URL
 * in the product. The build boundary is not worth an SEO migration.
 */
import { matchIn, type Route } from "@agitome/ui-shared"
import * as Tools from "./tools"
import * as Pages from "./pages"
import * as Content from "./content"

export type { Route }

// Re-exported so the prerenderer can reach it through the SSR bundle of this file. It seeds the
// preload cache before each page renders, which is what puts the fetched content INTO the HTML
// rather than leaving "Loading…" for a crawler to read.
export { seedPreload } from "@agitome/ui-shared"

export const ROUTES: Route[] = [
  { path: "/tools/vendors", title: "This page is about my product — what can I do?",
    description: "Dispute an incident, claim your subject, attach a permanent statement, assert a resolution. Three of the four need no account, and the 30-day clock runs in your favour.",
    element: Tools.VendorTools },

  { path: "/tools", title: "Free tools — agitome",
    description: "Twelve free tools for deciding whether an agent, framework or model is safe to ship. No account on any of them.",
    element: Pages.Tools },
  { path: "/tools/incidents", title: "The agent incident database",
    description: "Every reported agent failure — searchable by framework, model, failure mode, vendor or DID. Free, no account.",
    element: Pages.Incidents, loads: { incidents: "/incidents?limit=25&sort=newest" } },
  { path: "/tools/risk-profiles", title: "Risk profiles — every subject we track",
    description: "One page per framework, model and agent category, with the tier and the counts behind it.",
    element: Pages.ProfileIndex, loads: { profileKinds: "/risk-profiles" } },
  { path: "/tools/risk-profiles/:kind/:slug", title: "Risk profile — agitome",
    description: "Is this framework, model or agent category risky to deploy? The counts, the failure modes, the tier, and every rule that produced it.",
    element: Pages.Profile, perSubject: true },
  { path: "/tools/categories", title: "Browse by failure mode",
    description: "The closed taxonomy every product in the portfolio classifies against — so incident counts are comparable across subjects.",
    element: Pages.Categories,
    loads: { taxonomy: "/incident-taxonomy", drop: "/state-of-agent-risk" } },
  { path: "/tools/gate", title: "The admission gate — should you ship this?",
    description: "Run your own policy over reported data before an agent reaches production. Allow, review or deny, with every rule that fired printed beside it.",
    element: Tools.Gate },
  { path: "/tools/risk-report", title: "Pull a risk report on your whole stack",
    description: "Carfax for the stack you are about to ship. One shareable report across framework, model and vendor. Free, no account.",
    element: Tools.RiskReportTool },
  { path: "/tools/exposure", title: "What could an agent failure cost you?",
    description: "Arithmetic on your numbers, and an honest answer on whether anybody could underwrite it yet. Not a quote.",
    element: Tools.Exposure },
  { path: "/tools/state-of-agent-risk", title: "The State of Agent Risk",
    description: "The periodic public dataset on what has actually gone wrong with production AI agents — with the methodology printed beside it.",
    element: Pages.Drop, loads: { drop: "/state-of-agent-risk" } },
  { path: "/tools/report", title: "File an incident report",
    description: "Two fields, under a minute, no account. Tell us what your agent did.",
    element: Tools.FileReport },
  { path: "/tools/track-record", title: "Verify a track record in your browser",
    description: "Check an agent's track-record export yourself. It runs in this tab — the document never leaves your machine and there is no call back to agitome.",
    element: Content.TrackRecord },
  { path: "/tools/overlay", title: "Embed the risk badge",
    description: "A flat, cacheable risk badge for your own directory, docs or marketplace. An image — no runtime of ours on your page.",
    element: Content.Overlay },
  { path: "/tools/oracle", title: "On-chain attestations (ERC-8004)",
    description: "What agitome would assert in an on-chain attestation, what it deliberately would not, and why nothing is deployed yet.",
    element: Content.Oracle },
  { path: "/tools/unsubscribe", title: "Unsubscribe",
    description: "One click, no login, no confirmation email. Remove your address from everything agitome holds.",
    element: Tools.Unsubscribe },
]

export function match(pathname: string): Route | undefined {
  return matchIn(ROUTES, pathname)
}
